import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Display } from '@/types/display';

interface UseNearbyDisplaysOptions {
  latitude: number;
  longitude: number;
  radiusMiles?: number;
  excludeDisplayId?: string;
}

export const useNearbyDisplays = ({ latitude, longitude, radiusMiles = 25, excludeDisplayId }: UseNearbyDisplaysOptions) => {
  return useQuery({
    queryKey: ['nearby-displays', latitude, longitude, radiusMiles, excludeDisplayId],
    queryFn: async (): Promise<{ displays: Display[]; cities: string[] }> => {
      if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        return { displays: [], cities: [] };
      }
      // Convert miles to meters for PostGIS
      const radiusMeters = radiusMiles * 1609.34;
      // Use correct PostgREST spatial filter syntax
      let data, error;
      try {
        ({ data, error } = await supabase
          .from('displays')
          .select(`
            *,
            display_years(
              id,
              display_media(id)
            )
          `)
          .filter('latitude', 'not.is', null)
          .filter('longitude', 'not.is', null)
          .neq('id', excludeDisplayId || '')
          .filter('location', 'st_dwithin', `POINT(${longitude} ${latitude})`, radiusMeters)
          .limit(100)
        );
      } catch (err) {
        console.error('Spatial query failed:', err);
        throw new Error('Failed to fetch nearby displays (spatial query error)');
      }
      if (error) {
        console.error('Error fetching nearby displays:', error);
        throw new Error(error.message);
      }
      
      // Filter displays that have either an image_url OR associated media
      const filteredDisplays = (data || []).filter(display => {
        // Check if display has a main image
        const hasMainImage = display.image_url && display.image_url.trim() !== '';
        
        // Check if display has associated media through display_years
        const hasAssociatedMedia = display.display_years?.some((year: any) => 
          year.display_media && year.display_media.length > 0
        );
        
        // Show display only if it has either a main image or associated media
        return hasMainImage || hasAssociatedMedia;
      });
      
      const displays: Display[] = filteredDisplays;
      // Deduplicate cities
      const cities = Array.from(new Set(displays.map(d => d.location?.split(',')[0]?.trim()).filter(Boolean)));
      return { displays, cities };
    },
    enabled: typeof latitude === 'number' && typeof longitude === 'number',
  });
}; 